import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import {
  XMarkIcon,
  TrashIcon,
  EyeIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  CalendarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

interface ReportDetailModalProps {
  reportId: string | null;
  isOpen: boolean;
  onClose: () => void;
  isCoordinator?: boolean;
  onStatusUpdate: (id: string, status: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

interface Response {
  id: number;
  report_id: number;
  question_id: string;
  question_text: string;
  question_type: string;
  response_value: string;
  response_data: any;
  created_at: string;
}

interface ReportData {
  id: number;
  report_id: string;
  user_id: number;
  observer_name: string;
  observer_contact: string;
  observer_organization: string;
  district: string;
  constituency: string;
  ward: string;
  center: string;
  timeline: string;
  status: string;
  submission_method: string;
  location_lat: number | null;
  location_lng: number | null;
  created_at: string;
  updated_at: string;
  user_name: string;
  user_email: string;
  responses: Response[];
}

const ReportDetailModal: React.FC<ReportDetailModalProps> = ({
  reportId,
  isOpen,
  onClose,
  onStatusUpdate,
  isCoordinator = false,
  onDelete,
}) => {
  const [selectedTab, setSelectedTab] = useState<'overview' | 'responses'>('overview');
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: reportData, isLoading, error } = useQuery(
    ['report', reportId],
    () => reportId ? api.reports.getOne(reportId) : null,
    {
      enabled: !!reportId && isOpen,
    }
  );

  const report: ReportData | null = reportData?.data?.data || null;

  const handleStatusUpdate = async (status: string) => {
    if (report) {
      await onStatusUpdate(report.id.toString(), status);
    }
  };

  const handleDelete = async () => {
    if (report && window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      setIsDeleting(true);
      try {
        await onDelete(report.id.toString());
        onClose();
      } catch (error) {
        console.error('Failed to delete report:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      submitted: { class: 'bg-yellow-100 text-yellow-800', label: 'Submitted' },
      verifying: { class: 'bg-blue-100 text-blue-800', label: 'Verifying' },
      verified: { class: 'bg-green-100 text-green-800', label: 'Verified' },
      resolved: { class: 'bg-gray-100 text-gray-800', label: 'Resolved' },
      reviewed: { class: 'bg-purple-100 text-purple-800', label: 'Reviewed' },
      approved: { class: 'bg-green-100 text-green-800', label: 'Approved' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.submitted;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const getTimelineBadge = (timeline: string) => {
    const timelineConfig = {
      morning: { class: 'bg-blue-100 text-blue-800', label: 'Morning (6am-12pm)' },
      afternoon: { class: 'bg-yellow-100 text-yellow-800', label: 'Afternoon (12pm-4pm)' },
      vote_count: { class: 'bg-green-100 text-green-800', label: 'Vote Count (4pm-6am)' },
    };
    const config = timelineConfig[timeline as keyof typeof timelineConfig] || timelineConfig.morning;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const renderResponseValue = (response: Response) => {
    if (response.question_type === 'politicalparty' && response.response_data) {
      return (
        <div className="space-y-2">
          {response.response_data.map((party: any, index: number) => (
            <div key={index} className="p-3 rounded-lg bg-gray-50">
              <div className="font-medium text-gray-900">{party.partyName}</div>
              <div className="text-sm text-gray-600">
                Representatives: {party.numberOfRepresentatives}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return (
      <span className={`font-medium ${
        response.response_value === 'Yes' ? 'text-green-600' :
        response.response_value === 'No' ? 'text-red-600' :
        'text-gray-900'
      }`}>
        {response.response_value || 'N/A'}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative inline-block w-full overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl max-w-7xl">
          {/* Header */}
          <div className="px-6 py-4 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <DocumentTextIcon className="w-6 h-6 text-gray-400" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Report Details
                  </h3>
                  {report && (
                    <p className="text-sm text-gray-500">
                      {report.report_id}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 rounded-md hover:text-gray-500"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white">
            <>
              {isLoading && (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              )}

              {error && (
                <div className="p-6">
                  <div className="p-4 border border-red-200 rounded-md bg-red-50">
                    <div className="flex">
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Error loading report
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          Failed to load report details. Please try again.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {report && (
                <>
                  {/* Tabs */}
                  <div className="border-b border-gray-200">
                  <nav className="flex px-6 -mb-px">
                    <button
                      onClick={() => setSelectedTab('overview')}
                      className={`py-4 px-4 border-b-2 font-medium text-sm ${
                        selectedTab === 'overview'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setSelectedTab('responses')}
                      className={`py-4 px-4 border-b-2 font-medium text-sm ${
                        selectedTab === 'responses'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Responses ({report.responses.length})
                    </button>
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6 overflow-y-auto max-h-96">
                  {selectedTab === 'overview' && (
                    <div className="space-y-6">
                      {/* Report Info */}
                      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Observer Information */}
                        <div className="p-4 rounded-lg bg-gray-50">
                          <h4 className="flex items-center mb-3 text-sm font-medium text-gray-900">
                            <UserIcon className="w-4 h-4 mr-2" />
                            Observer Information
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">Name:</span>
                              <span className="ml-2 font-medium">{report.observer_name}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Contact:</span>
                              <span className="ml-2">{report.observer_contact || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Organization:</span>
                              <span className="ml-2">{report.observer_organization || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Submitted by:</span>
                              <span className="ml-2">{report.user_name}</span>
                            </div>
                          </div>
                        </div>

                        {/* Location Information */}
                        <div className="p-4 rounded-lg bg-gray-50">
                          <h4 className="flex items-center mb-3 text-sm font-medium text-gray-900">
                            <MapPinIcon className="w-4 h-4 mr-2" />
                            Location Information
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">District:</span>
                              <span className="ml-2 font-medium">{report.district}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Constituency:</span>
                              <span className="ml-2">{report.constituency}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Ward:</span>
                              <span className="ml-2">{report.ward}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Center:</span>
                              <span className="ml-2">{report.center}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Report Details */}
                      <div className="p-4 rounded-lg bg-gray-50">
                        <h4 className="flex items-center mb-3 text-sm font-medium text-gray-900">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          Report Details
                        </h4>
                        <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2 lg:grid-cols-3">
                          <div>
                            <span className="text-gray-500">Timeline:</span>
                            <div className="mt-1">
                              {getTimelineBadge(report.timeline)}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Status:</span>
                            <div className="mt-1">
                              {getStatusBadge(report.status)}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Submission Method:</span>
                            <span className="ml-2 font-medium">{report.submission_method}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Created:</span>
                            <span className="ml-2">{new Date(report.created_at).toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Updated:</span>
                            <span className="ml-2">{new Date(report.updated_at).toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Responses:</span>
                            <span className="ml-2 font-medium">{report.responses.length}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedTab === 'responses' && (
                    <div className="space-y-4">
                      {report.responses.map((response) => (
                        <div key={response.id} className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {response.question_id}
                                </span>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  response.question_type === 'yesno' ? 'bg-blue-100 text-blue-800' :
                                  response.question_type === 'number' ? 'bg-green-100 text-green-800' :
                                  response.question_type === 'politicalparty' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {response.question_type}
                                </span>
                              </div>
                              <p className="mt-1 text-sm text-gray-700">
                                {response.question_text}
                              </p>
                              <div className="mt-2">
                                {renderResponseValue(response)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            </>
          </div>

          {/* Footer with Action Buttons */}
          {report && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex space-x-3">
                  {/* Status Update Buttons */}
                  {report.status === 'submitted' && (
                    <button
                      onClick={() => handleStatusUpdate('verifying')}
                      disabled={isCoordinator}
                      title={isCoordinator ? 'Coordinators have view-only access' : 'Mark as Verifying'}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                      aria-disabled={isCoordinator}
                    >
                      <ClockIcon className="w-4 h-4 mr-2" />
                      Mark as Verifying
                    </button>
                  )}
                  
                  {report.status === 'verifying' && (
                    <button
                      onClick={() => handleStatusUpdate('verified')}
                      disabled={isCoordinator}
                      title={isCoordinator ? 'Coordinators have view-only access' : 'Mark as Verified'}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
                      aria-disabled={isCoordinator}
                    >
                      <CheckIcon className="w-4 h-4 mr-2" />
                      Mark as Verified
                    </button>
                  )}
                  
                  {(report.status === 'verified' || report.status === 'verifying') && (
                    <button
                      onClick={() => handleStatusUpdate('resolved')}
                      disabled={isCoordinator}
                      title={isCoordinator ? 'Coordinators have view-only access' : 'Mark as Resolved'}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-white bg-gray-600 border border-transparent rounded-md hover:bg-gray-700 disabled:opacity-50"
                      aria-disabled={isCoordinator}
                    >
                      <CheckIcon className="w-4 h-4 mr-2" />
                      Mark as Resolved
                    </button>
                  )}
                </div>

                <div className="flex space-x-3">
                  {/* Delete Button */}
                  {!isCoordinator && (
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  )}

                  {/* Close Button */}
                  <button
                    onClick={onClose}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportDetailModal;